import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Download, School, Search } from 'lucide-react';
import { downloadXlsx, slugify, type XlsxColumn } from '../../../shared/utils/xlsxExport';

/**
 * School-wise pass percentage table, shared by the Preparatory (3–5),
 * Middle (6–8) and Secondary (9 & 11) region drill-downs.
 *
 * Pass % is `passed / (passed + failed)` — rows whose result was never declared
 * are counted in their own column and kept OUT of the denominator, so a school
 * with incomplete entry is not penalised as if those students had failed.
 * A class with nothing declared shows "—", never 0%.
 */

export interface PassFailCounts {
  passed: number;
  failed: number;
  notDeclared: number;
  /** 0–100, or null when nothing was declared */
  passPercentage: number | null;
}

export interface ClassPassMetrics extends PassFailCounts {
  totalStudents: number;
  avgMarks: number;
}

export interface SchoolPassRow extends PassFailCounts {
  kvID: number;
  schoolID: number | null;
  schoolName: string | null;
  totalStudents: number;
  avgMarks: number;
  byClass?: Record<string, ClassPassMetrics | null>;
}

interface Props {
  schools: SchoolPassRow[];
  /** Classes owned by this stage, in display order — e.g. [9, 11] */
  classes: number[];
  stageLabel: string;
  regionName: string;
  year: number;
  /** Optional term caption — omit for stages whose data has no term dimension */
  termLabel?: string;
  onRowClick?: (school: SchoolPassRow) => void;
  /** Row entity noun — 'School' (default) or 'Region' when rows are ROs */
  entityLabel?: string;
  /** Label for the row id (kvID field) — 'KV ID' (default) or 'RO ID' */
  idLabel?: string;
  /** Footer/total-row label — defaults to 'Region Total' */
  totalRowLabel?: string;
  /** First line of the export's Notes sheet describing the data source */
  sourceNote?: string;
}

type SortDir = 'asc' | 'desc';

const pctText = (pct: number | null): string => (pct === null ? '—' : `${pct.toFixed(2)}%`);

const pctClass = (pct: number | null): string => {
  if (pct === null) return 'text-gray-300';
  if (pct >= 90) return 'text-emerald-600 font-semibold';
  if (pct >= 75) return 'text-indigo-600 font-semibold';
  if (pct >= 60) return 'text-amber-600 font-semibold';
  return 'text-red-600 font-semibold';
};

const sumPct = (passed: number, failed: number): number | null => {
  const declared = passed + failed;
  return declared > 0 ? Number(((passed / declared) * 100).toFixed(2)) : null;
};

const emptyMetrics: ClassPassMetrics = {
  totalStudents: 0, avgMarks: 0, passed: 0, failed: 0, notDeclared: 0, passPercentage: null,
};

const classOf = (s: SchoolPassRow, cn: number): ClassPassMetrics | null => s.byClass?.[String(cn)] ?? null;

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/**
 * A backend that predates the pass/fail fields sends rows without
 * passed/failed/notDeclared/passPercentage — render those as zeros/dashes
 * instead of crashing on `undefined.toLocaleString()`.
 */
const normalizeMetrics = <T extends ClassPassMetrics>(m: Partial<T> | null | undefined): ClassPassMetrics | null => {
  if (!m) return null;
  const passed = num(m.passed);
  const failed = num(m.failed);
  return {
    totalStudents: num(m.totalStudents),
    avgMarks: num(m.avgMarks),
    passed,
    failed,
    notDeclared: num(m.notDeclared),
    passPercentage: typeof m.passPercentage === 'number' ? m.passPercentage : sumPct(passed, failed),
  };
};

const normalizeRow = (s: SchoolPassRow): SchoolPassRow => ({
  ...s,
  ...(normalizeMetrics(s) as ClassPassMetrics),
  byClass: s.byClass
    ? Object.fromEntries(Object.entries(s.byClass).map(([k, v]) => [k, normalizeMetrics(v)]))
    : undefined,
});

const SchoolPassPercentTable: React.FC<Props> = ({
  schools, classes, stageLabel, regionName, year, termLabel, onRowClick,
  entityLabel = 'School',
  idLabel = 'KV ID',
  totalRowLabel = 'Region Total',
  sourceNote = 'Source: kvs_mis.student_previous_result_details, joined to master_kvs for school names.',
}) => {
  const entityLower = entityLabel.toLowerCase();
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<string>('overallPct');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [exporting, setExporting] = useState(false);

  const safeSchools = useMemo(() => schools.map(normalizeRow), [schools]);

  const filtered = useMemo(() => {
    if (!search.trim()) return safeSchools;
    const q = search.trim().toLowerCase();
    return safeSchools.filter(
      (s) => (s.schoolName ?? '').toLowerCase().includes(q) || String(s.kvID).includes(q),
    );
  }, [safeSchools, search]);

  const sorted = useMemo(() => {
    const valueOf = (s: SchoolPassRow): number | string => {
      if (sortKey === 'schoolName') return (s.schoolName ?? '').toLowerCase();
      if (sortKey === 'kvID') return s.kvID;
      if (sortKey === 'overallPct') return s.passPercentage ?? -1;
      if (sortKey === 'overallTotal') return s.totalStudents;
      const [kind, cnRaw] = sortKey.split(':');
      const m = classOf(s, Number(cnRaw));
      if (kind === 'classPct') return m?.passPercentage ?? -1;
      if (kind === 'classTotal') return m?.totalStudents ?? -1;
      return 0;
    };
    return [...filtered].sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      let cmp: number;
      if (typeof va === 'string' || typeof vb === 'string') cmp = String(va).localeCompare(String(vb));
      else cmp = va - vb;
      if (cmp === 0) cmp = (a.schoolName ?? '').localeCompare(b.schoolName ?? '');
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  /** Totals reflect the FILTERED set, so a search narrows the footer too. */
  const totals = useMemo(() => {
    const perClass = classes.map((cn) => {
      const rows = sorted.map((s) => classOf(s, cn)).filter((m): m is ClassPassMetrics => m !== null);
      const passed = rows.reduce((sum, m) => sum + m.passed, 0);
      const failed = rows.reduce((sum, m) => sum + m.failed, 0);
      return {
        cn,
        totalStudents: rows.reduce((sum, m) => sum + m.totalStudents, 0),
        passed,
        failed,
        notDeclared: rows.reduce((sum, m) => sum + m.notDeclared, 0),
        passPercentage: sumPct(passed, failed),
      };
    });
    const passed = sorted.reduce((sum, s) => sum + s.passed, 0);
    const failed = sorted.reduce((sum, s) => sum + s.failed, 0);
    return {
      perClass,
      totalStudents: sorted.reduce((sum, s) => sum + s.totalStudents, 0),
      passed,
      failed,
      notDeclared: sorted.reduce((sum, s) => sum + s.notDeclared, 0),
      passPercentage: sumPct(passed, failed),
    };
  }, [sorted, classes]);

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'schoolName' ? 'asc' : 'desc');
    }
  };

  const SortHeader: React.FC<{ label: string; sortId: string; align?: string; className?: string }> = ({
    label, sortId, align = 'text-center', className = '',
  }) => (
    <th
      onClick={() => toggleSort(sortId)}
      className={`px-3 py-2.5 ${align} text-[11px] font-semibold text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-indigo-600 transition ${className}`}
      title={`Sort by ${label}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === sortId &&
          (sortDir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
      </span>
    </th>
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const columns: XlsxColumn[] = [
        { header: 'S.No', width: 6, type: 'number' },
        { header: idLabel, width: 9, type: 'number' },
        { header: `${entityLabel} Name`, width: 46, type: 'text' },
        ...classes.flatMap((cn): XlsxColumn[] => [
          { header: `Class ${cn} Total`, width: 11, type: 'number' },
          { header: `Class ${cn} Pass`, width: 11, type: 'number' },
          { header: `Class ${cn} Fail`, width: 11, type: 'number' },
          { header: `Class ${cn} Not Declared`, width: 13, type: 'number' },
          { header: `Class ${cn} Pass %`, width: 12, type: 'percent' },
        ]),
        { header: 'Total', width: 10, type: 'number' },
        { header: 'Pass', width: 10, type: 'number' },
        { header: 'Fail', width: 10, type: 'number' },
        { header: 'Not Declared', width: 13, type: 'number' },
        { header: 'Avg Marks', width: 11, type: 'decimal' },
        { header: 'Overall Pass %', width: 14, type: 'percent' },
      ];

      // Excel percent cells hold fractions; the backend sends 0–100.
      const asFraction = (pct: number | null) => (pct === null ? null : pct / 100);

      const rows = sorted.map((s, i) => [
        i + 1,
        s.kvID,
        s.schoolName ?? `(unmapped ${idLabel} ${s.kvID})`,
        ...classes.flatMap((cn) => {
          const m = classOf(s, cn);
          // A school that does not run this class exports blanks, not zeros.
          if (!m) return [null, null, null, null, null];
          return [m.totalStudents, m.passed, m.failed, m.notDeclared, asFraction(m.passPercentage)];
        }),
        s.totalStudents,
        s.passed,
        s.failed,
        s.notDeclared,
        s.avgMarks,
        asFraction(s.passPercentage),
      ]);

      const totalRow = [
        null,
        null,
        `${totalRowLabel.toUpperCase()} (${sorted.length} ${entityLower}${sorted.length === 1 ? '' : 's'})`,
        ...totals.perClass.flatMap((t) => [
          t.totalStudents, t.passed, t.failed, t.notDeclared, asFraction(t.passPercentage),
        ]),
        totals.totalStudents,
        totals.passed,
        totals.failed,
        totals.notDeclared,
        null,
        asFraction(totals.passPercentage),
      ];

      const notes: XlsxColumn[] = [{ header: 'Note', width: 110, type: 'text' }];
      const noteRows: string[][] = [
        [sourceNote],
        ['Pass % = Pass / (Pass + Fail). Rows with no declared result are excluded from the denominator.'],
        ['Result codes: 1 = Pass, 2 = Fail, 0 = Not declared.'],
        [`A blank Pass % means the ${entityLower} had no declared result for that class — it is not 0%.`],
        [`Scope: ${regionName}, ${stageLabel}, classes ${classes.join(', ')}${termLabel ? `, ${termLabel}` : ''}, year ${year}.`],
        search.trim() ? [`Filtered by search: "${search.trim()}" — totals reflect the filtered set only.`] : [],
      ].filter((r) => r.length > 0);

      await downloadXlsx(
        `${slugify(regionName)}-${slugify(stageLabel)}-pass-percentage-${year}`,
        [
          {
            name: `${stageLabel} Pass %`,
            columns,
            rows,
            totalRow,
            titleLines: [
              `${regionName} — ${stageLabel} ${entityLabel}-wise Pass Percentage`,
              `Classes ${classes.join(' & ')}${termLabel ? ` · ${termLabel}` : ''} · Academic Year ${year} · ${sorted.length} ${entityLower}s`,
            ],
          },
          { name: 'Notes & Source', columns: notes, rows: noteRows },
        ],
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-gray-900">{entityLabel}-wise Pass Percentage</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Classes {classes.join(' & ')}{termLabel ? ` · ${termLabel}` : ''} · declared results only
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${entityLower} or ${idLabel}…`}
              className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 w-52 bg-white"
            />
          </div>
          <button
            onClick={handleExport}
            disabled={exporting || sorted.length === 0}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            title="Download this table as an Excel workbook"
          >
            <Download size={13} />
            {exporting ? 'Preparing…' : 'Export Excel'}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        {sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <School size={32} className="mb-2 opacity-40" />
            <p className="text-sm">No {entityLower}s found</p>
          </div>
        ) : (
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase w-10">#</th>
                <SortHeader label={entityLabel} sortId="schoolName" align="text-left" />
                {classes.map((cn) => (
                  <React.Fragment key={cn}>
                    <SortHeader label={`C${cn} Total`} sortId={`classTotal:${cn}`} className="border-l border-gray-100" />
                    <SortHeader label={`C${cn} Pass %`} sortId={`classPct:${cn}`} />
                  </React.Fragment>
                ))}
                <SortHeader label="Total" sortId="overallTotal" className="border-l border-gray-100" />
                <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase">Pass</th>
                <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase">Fail</th>
                <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase" title="Result not declared — excluded from the Pass % denominator">ND</th>
                <SortHeader label="Overall Pass %" sortId="overallPct" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sorted.map((s, idx) => (
                <tr
                  key={`${s.kvID}-${idx}`}
                  onClick={onRowClick ? () => onRowClick(s) : undefined}
                  className={`hover:bg-gray-50 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                >
                  <td className="px-3 py-2.5 text-xs text-gray-400 font-medium">{idx + 1}</td>
                  <td className="px-3 py-2.5">
                    <p className="text-sm font-semibold text-gray-900 leading-tight">
                      {s.schoolName ?? `(unmapped ${idLabel} ${s.kvID})`}
                    </p>
                    <p className="text-[11px] text-gray-400">{idLabel} {s.kvID}</p>
                  </td>
                  {classes.map((cn) => {
                    const m = classOf(s, cn) ?? emptyMetrics;
                    const hasClass = classOf(s, cn) !== null;
                    return (
                      <React.Fragment key={cn}>
                        <td className="px-3 py-2.5 text-center text-sm text-gray-700 border-l border-gray-100">
                          {hasClass ? m.totalStudents.toLocaleString() : '—'}
                        </td>
                        <td className={`px-3 py-2.5 text-center text-sm ${pctClass(m.passPercentage)}`}>
                          {pctText(m.passPercentage)}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td className="px-3 py-2.5 text-center text-sm text-gray-700 border-l border-gray-100">{s.totalStudents.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-center text-sm text-emerald-700">{s.passed.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-center text-sm text-red-600">{s.failed.toLocaleString()}</td>
                  <td className={`px-3 py-2.5 text-center text-sm ${s.notDeclared > 0 ? 'text-amber-600 font-medium' : 'text-gray-300'}`}>
                    {s.notDeclared.toLocaleString()}
                  </td>
                  <td className={`px-3 py-2.5 text-center text-sm ${pctClass(s.passPercentage)}`}>{pctText(s.passPercentage)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-amber-50 border-t-2 border-amber-200 font-semibold">
                <td className="px-3 py-2.5" />
                <td className="px-3 py-2.5 text-sm text-gray-900">
                  {totalRowLabel} <span className="font-normal text-gray-500">({sorted.length} {entityLower}s)</span>
                </td>
                {totals.perClass.map((t) => (
                  <React.Fragment key={t.cn}>
                    <td className="px-3 py-2.5 text-center text-sm text-gray-900 border-l border-amber-200">{t.totalStudents.toLocaleString()}</td>
                    <td className={`px-3 py-2.5 text-center text-sm ${pctClass(t.passPercentage)}`}>{pctText(t.passPercentage)}</td>
                  </React.Fragment>
                ))}
                <td className="px-3 py-2.5 text-center text-sm text-gray-900 border-l border-amber-200">{totals.totalStudents.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-center text-sm text-emerald-700">{totals.passed.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-center text-sm text-red-600">{totals.failed.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-center text-sm text-amber-700">{totals.notDeclared.toLocaleString()}</td>
                <td className={`px-3 py-2.5 text-center text-sm ${pctClass(totals.passPercentage)}`}>{pctText(totals.passPercentage)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      <div className="px-5 py-2.5 bg-gray-50 border-t border-gray-100">
        <p className="text-[11px] text-gray-500">
          Pass % = Pass / (Pass + Fail). <span className="font-medium">ND</span> = result not declared, excluded from the denominator.
          A dash means no declared result for that class — not 0%.
        </p>
      </div>
    </div>
  );
};

export default SchoolPassPercentTable;
